import { defineStore } from 'pinia';
import type { SelectOption } from 'naive-ui';
import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import { SetupStoreId } from '@/enum';
import { fetchOverviewAuthorizedApps, fetchOverviewChannels, fetchOverviewCountries } from '@/service/api';
import { normalizeChannelList } from '@/hooks/business/system-manage-cache';
import { toAppOptions, toCountryOptions, toStringOptions } from '@/components/overview/shared';
import { useAppStore } from '../app';
import { getLastWeek, getToday } from '@/utils/date-picker';

type OverviewAppType = 'default' | 'abtest_app' | 'custom_event';
type OverviewChannelType = 'authority' | 'all';

export const useQueryParmStore = defineStore(SetupStoreId.QueryParm, () => {
  const appStore = useAppStore();
  const appids: Ref<SelectOption[]> = ref([]);
  const appidOptionsByType: Ref<Record<OverviewAppType, SelectOption[]>> = ref({
    default: [],
    abtest_app: [],
    custom_event: []
  });
  const countryData: Ref<Api.Statistic.CountryName[]> = ref([]);
  const countries = computed<SelectOption[]>(() => toCountryOptions(countryData.value, appStore.locale));
  const channels: Ref<SelectOption[]> = ref([]);
  const channelOptionsByType: Ref<Record<OverviewChannelType, SelectOption[]>> = ref({
    authority: [],
    all: []
  });

  // 全局统一的自然日日期范围：7 天前至今天。
  const selectedDateRange: Ref<number[]> = ref([getLastWeek(), getToday()]);
  const selectedAppids: Ref<string[]> = ref([]);
  const selectedAppidsByType: Ref<Record<OverviewAppType, string[]>> = ref({
    default: [],
    abtest_app: [],
    custom_event: []
  });
  const selectedCountries: Ref<string[]> = ref([]);
  const selectedChannels: Ref<string[]> = ref([]);
  const selectedChannelsByType: Ref<Record<OverviewChannelType, string[]>> = ref({
    authority: [],
    all: []
  });

  function setSelectedAppids(value: string[]) {
    selectedAppids.value = value;
    selectedAppidsByType.value.default = value;
  }

  function getSelectedAppids(type: OverviewAppType = 'default') {
    if (type === 'default') return selectedAppids.value;
    return selectedAppidsByType.value[type] ?? [];
  }

  function setSelectedAppidsByType(type: OverviewAppType = 'default', value: string[]) {
    selectedAppidsByType.value[type] = value;
    if (type === 'default') selectedAppids.value = value;
  }

  function getSelectedChannels(type: OverviewChannelType = 'all') {
    if (type === 'all') return selectedChannels.value;
    return selectedChannelsByType.value[type] ?? [];
  }

  function setSelectedChannelsByType(type: OverviewChannelType = 'all', value: string[]) {
    selectedChannelsByType.value[type] = value;
    if (type === 'all') selectedChannels.value = value;
  }

  function toTypedAppOptions(data: Api.Application.Application[] = [], type: OverviewAppType = 'default') {
    const options = toAppOptions(data);
    if (type !== 'custom_event') return options;

    return options.map(option => {
      const app = data.find(item => String(item.id) === String(option.value));
      const record = app as Record<string, unknown> | undefined;
      const appid = record?.appId ?? record?.appid;

      return {
        ...option,
        value: String(appid ?? option.value)
      };
    });
  }

  async function setOverviewAppOptionsAsync(type: OverviewAppType = 'default') {
    if (appidOptionsByType.value[type].length !== 0) return;

    const { error, data } = await fetchOverviewAuthorizedApps(type === 'default' ? undefined : type);
    if (!error) {
      appidOptionsByType.value[type] = toTypedAppOptions(data ?? [], type);
      if (type === 'default') appids.value = appidOptionsByType.value[type];
    }
  }

  async function setOverviewCountryOptionsAsync() {
    if (countryData.value.length !== 0) return;

    const { error, data } = await fetchOverviewCountries();
    if (!error) {
      countryData.value = data ?? [];
    }
  }

  function getCountryDisplayName(countryCode: string) {
    const normalizedCode = countryCode.toLowerCase();
    const country = countryData.value.find(item => item.abbr.toLowerCase() === normalizedCode);
    if (!country) return countryCode;

    return appStore.locale === 'en-US' ? country.en : `${country.chn} ${country.abbr}`;
  }

  async function setOverviewChannelOptionsAsync(type: OverviewChannelType = 'all') {
    if (channelOptionsByType.value[type].length !== 0) return;

    const { error: allError, data: allData } = await fetchOverviewChannels(type);
    let channelList: string[] = [];
    if (!allError && allData) {
      channelList = normalizeChannelList(allData);
    }

    if (!channelList.length && type === 'all') {
      const { error, data } = await fetchOverviewChannels('authority');
      if (!error && data) {
        channelList = normalizeChannelList(data);
      }
    }

    if (channelList.length) {
      channelOptionsByType.value[type] = toStringOptions(channelList);
      if (type === 'all') channels.value = channelOptionsByType.value[type];
    }
  }

  return {
    appids,
    appidOptionsByType,
    countryData,
    countries,
    channels,
    channelOptionsByType,
    selectedDateRange,
    selectedAppids,
    selectedAppidsByType,
    selectedCountries,
    selectedChannels,
    selectedChannelsByType,
    setSelectedAppids,
    getSelectedAppids,
    setSelectedAppidsByType,
    getSelectedChannels,
    setSelectedChannelsByType,
    setOverviewAppOptionsAsync,
    setOverviewCountryOptionsAsync,
    getCountryDisplayName,
    setOverviewChannelOptionsAsync
  };
});
